import { Component, ChangeDetectionStrategy, input, output, computed, inject } from '@angular/core';

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { DrawerMode } from '../drawer/drawer.types';

/**
 * Sidebar Component
 *
 * A specialized drawer variant positioned on the right side, with predefined
 * slots for header, body, and footer content. Uses content projection for
 * flexible composition.
 *
 * @example
 * ```html
 * <zag-sidebar [open]="isOpen" [mode]="'push'" [width]="'360px'">
 *   <div sidebar-header>
 *     <h2>Sidebar Title</h2>
 *   </div>
 *   <div sidebar-body>
 *     <p>Main content here</p>
 *   </div>
 *   <div sidebar-footer>
 *     <button>Close</button>
 *   </div>
 * </zag-sidebar>
 * ```
 */
@Component({
  selector: 'zag-sidebar',
  imports: [],
  template: `
    <!-- Backdrop for overlay mode -->
    @if (showBackdropComputed() && open()) {
      <div class="zag-sidebar__backdrop" (click)="onBackdropClick()" [attr.aria-hidden]="true"></div>
    }

    <!-- Sidebar panel -->
    <aside
      class="zag-sidebar__panel"
      [class.zag-sidebar__panel--open]="open()"
      [style.width]="effectiveWidth()"
      role="complementary"
      [attr.aria-hidden]="!open()"
    >
      <!-- Header slot -->
      <header class="zag-sidebar__header">
        <ng-content select="[sidebar-header]" />
      </header>

      <!-- Body slot (scrollable) -->
      <div class="zag-sidebar__body">
        <ng-content select="[sidebar-body]" />
      </div>

      <!-- Footer slot -->
      <footer class="zag-sidebar__footer">
        <ng-content select="[sidebar-footer]" />
      </footer>
    </aside>
  `,
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.zag-sidebar]': 'true',
    '[class.zag-sidebar--open]': 'open()',
    '[class.zag-sidebar--push]': 'mode() === "push"',
    '[class.zag-sidebar--overlay]': 'mode() === "overlay"',
    '[class.zag-sidebar--mobile]': 'isMobile()',
    '[style.--sidebar-content-width]': 'effectiveWidth()',
  },
})
export class SidebarComponent {
  private readonly breakpointObserver = inject(BreakpointObserver);

  // Inputs
  readonly open = input<boolean>(false);
  readonly mode = input<DrawerMode>('push');
  readonly width = input<string>('320px');
  readonly showBackdrop = input<boolean>(true);

  // Outputs
  readonly openChange = output<boolean>();
  readonly backdropClick = output<void>();

  // Reactive breakpoint signal
  private readonly breakpointState = toSignal(
    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(map(result => result.matches)),
    { initialValue: false }
  );

  // Computed: Is mobile viewport?
  readonly isMobile = computed(() => this.breakpointState());

  // Computed: Show backdrop only in overlay mode or on mobile
  readonly showBackdropComputed = computed(() => {
    return this.showBackdrop() && (this.mode() === 'overlay' || this.isMobile());
  });

  // Computed: Effective width (full width on mobile)
  readonly effectiveWidth = computed(() => {
    return this.isMobile() ? '100%' : this.width();
  });

  onBackdropClick(): void {
    this.backdropClick.emit();
    this.openChange.emit(false);
  }
}
